<template>
    <div class="nominee-shareholder">
        <index 
            :payload-for-nominee-shareholder-table="payloadForNomineeShareholderTable"
            :payload-for-pending-change-nominator-table="payloadForPendingChangeNominatorTable"
            @add="add"
            @view="view"
            @edit="edit"
            @remove="remove"
            @viewPendingChangeNominator="viewPendingChangeNominator"
            @approvePendingChangeNominator="approvePendingChangeNominator"
        />

        <create-or-view-or-edit
            ref="createOrEdit"
            v-model="isNomineeShareholderSidebarActive"
            :data-status="status"
            :data-sidebar-action-loading="isSideBarActionLoading"
            :data-nominee-shareholder="nomineeShareholder"
            :auto-fill-requested-shareholder-nominator="autoFillRequestedShareholderNominator"
            @add-nominee-shareholder="create"
            @update-nominee-shareholder="update"
            @request-autofill-shareholder-nominator-data="requestAutofillShareholderNominatorData"
        />

        <create-or-view-change-shareholder-nominator-form
            ref="createOrViewChangeNominator"
            v-model="isGenerateDocumentSidebarActive"
            :data-status="status"
            :data-to-view="dataToViewChangeNominator"
            :data-sidebar-action-loading="isSideBarActionLoading"
        />

        <!-- delete -->
        <confirmation-modal  ref="modal">
            <template slot="body">
                <div>
                    <feather-icon class="d-block mx-auto text-warning" icon="AlertCircleIcon" size="90"/> 
                    <h1 class="text-center text-secondary display-5 mt-2">Are you sure?</h1>
                    <p class="text-center mt-2"> You won't be able to revert this! </p>
                </div>
            </template>

            <template slot="footer">
                <b-button class="d-flex" :disabled="isSideBarActionLoading" size="md" variant="primary" @click="confirmDelete">
                    Yes, delete it!
                    <normal-loader v-if="isSideBarActionLoading" class="left-10%" />
                </b-button>

                <b-button size="md" variant="outline-danger" @click="cancelDelete">
                    Cancel
                </b-button>
            </template>
        </confirmation-modal>
    </div>
</template>

<script>
import {
    BButton,
} from "bootstrap-vue";
import { mapGetters } from "vuex";
import { ref } from '@vue/composition-api';
import { errorToast, successToast } from "@/views/helpers/toast";
import ConfirmationModal from '@/views/components/utils/ConfirmationModal.vue';
import CompanyNomineeShareholder from '@/models/CompanyNomineeShareholder';
import { generateRandomUniqueId } from '@/helpers';
import { NOMINATOR_CHANGE_APPROVE_SUCCESS, NOMINEE_SHAREHOLDER_CREATE_SUCCESS, NOMINEE_SHAREHOLDER_DELETE_SUCCESS, NOMINEE_SHAREHOLDER_UPDATE_SUCCESS } from '@/constants/success';
import { REFRESH_AND_TRY_AGAIN_ERROR, SOMETHING_WENT_WRONG } from '@/constants/errors';
import User from '@/models/User';
import createOrViewOrEdit from './CreateOrViewOrEdit.vue';
import CreateOrViewChangeShareholderNominatorForm from '../../documents/prepare-documents/director-resolutions/CreateOrViewChangeShareholderNominatorForm.vue';
import Index from './Index.vue';
import useNomineeShareholdersTable from './useNomineeShareholdersTable';
import usePendingApprovalChangeNominatorTable from './usePendingApprovalChangeNominatorTable';
import GenerateDocument from "@/models/GenerateDocument";

export default {
    components: {
        Index,
        BButton,
        createOrViewOrEdit,
        ConfirmationModal,

        CreateOrViewChangeShareholderNominatorForm,
    },

    data() {
        return {
            status: undefined,
            nomineeShareholder: {},
            dataToViewChangeNominator: {},
            isNomineeShareholderSidebarActive: false,
            isGenerateDocumentSidebarActive: false,
            isSideBarActionLoading: false,
            autoFillRequestedShareholderNominator: {},

            nomineeShareholderBluePrint : {
                alias: null,
                uen: null,
            },
        };
    },

    computed: {
        ...mapGetters({
            isUserAdmin: "auth/isAdmin",
        }),
    },

    mounted()
    {
        // just to make sure the menu bar is shown
        this.$store.commit('appConfig/UPDATE_NAV_MENU_HIDDEN', false);
    },

    methods:{
        add(){
            this.nomineeShareholder = this.nomineeShareholderBluePrint;
            this.status = "create";
            this.isNomineeShareholderSidebarActive = true;
        },

        view(data){
            this.nomineeShareholder = data.item;
            this.status = "view";
            this.isNomineeShareholderSidebarActive = true;
        },

        edit(data){
            this.nomineeShareholder = data.item;
            this.status = "edit";
            this.isNomineeShareholderSidebarActive = true;
        },

        viewPendingChangeNominator(data) {
            this.dataToViewChangeNominator = data.item;
            this.status = "view";
            this.isGenerateDocumentSidebarActive = true;
        },

        async requestAutofillShareholderNominatorData(nominator)
        {
            if (nominator.id && nominator.type) {
                const data = await User.getByNRIC(nominator.id, nominator.type);
                this.autoFillRequestedShareholderNominator = data;
            }
        },
    },

    setup() {
        const filterMode = ref(false);
        const dataToDelete = ref(undefined);
        
        const {
            // table data
            sortBy,
            perPage,
            isBusy,
            dataMeta,
            currentPage,
            searchQuery,
            tableColumns,
            isSortDirDesc,
            perPageOptions,

            // UI
            isLoading,

            nomineeShareholders,
            pendingApprovalChangeNominators,
            refetchData,
            localizeData,
            fetchNomineeShareholders,
            refNomineeShareholderListTable,
            totalNomineeShareholders
        } = useNomineeShareholdersTable();

        const payloadForNomineeShareholderTable = {
            refetchData,
            sortBy,
            perPage,
            isBusy,
            dataMeta,
            currentPage,
            searchQuery,
            tableColumns,
            isSortDirDesc,
            isLoading,
            perPageOptions,
            nomineeShareholders,
            pendingApprovalChangeNominators,
            fetchNomineeShareholders,
            totalNomineeShareholders,
            refNomineeShareholderListTable,
        };

        // change nominator
        const {
            sortPendingApprovalNominatorBy,
            isPendingApprovalNominatorBusy,
            pendingApprovalNominatorPerPage,
            PendingApprovalNominatorDataMeta,
            isLoadingPendingApprovalNominator,
            pendingApprovalNominatorSearchQuery,
            pendingApprovalNominatorCurrentPage,
            pendingApprovalNominatorTableColumns,
            isSortDirDescPendingApprovalNominator,
            totalPendingApprovalNominator,
            pendingApprovalNominatorPerPageOptions,
            refPendingApprovalNominatorListTable,

            pendingApprovalNominator,
            refetchPendingApprovalNominatorData,
            filterOnPendingApprovalNominator,
        } = usePendingApprovalChangeNominatorTable();

        const payloadForPendingChangeNominatorTable = {
            sortPendingApprovalNominatorBy,
            isPendingApprovalNominatorBusy,
            pendingApprovalNominatorPerPage,
            PendingApprovalNominatorDataMeta,
            isLoadingPendingApprovalNominator,
            pendingApprovalNominatorSearchQuery,
            pendingApprovalNominatorCurrentPage,
            pendingApprovalNominatorTableColumns,
            isSortDirDescPendingApprovalNominator,
            totalPendingApprovalNominator,
            pendingApprovalNominatorPerPageOptions,
            refPendingApprovalNominatorListTable,

            pendingApprovalNominator,
            refetchPendingApprovalNominatorData,
            filterOnPendingApprovalNominator,
        };

        function create(nomineeShareholder){
            if (this.isUserAdmin)
            {
                this.isSideBarActionLoading = true;
                
                // eslint-disable-next-line no-param-reassign
                nomineeShareholder._id = generateRandomUniqueId();

                CompanyNomineeShareholder.create(nomineeShareholder).then(newNomineeShareholder => {
                    this.isNomineeShareholderSidebarActive = false;
                    this.isSideBarActionLoading = false;
                    
                    refNomineeShareholderListTable.value.localItems.push(localizeData(newNomineeShareholder));

                    successToast(NOMINEE_SHAREHOLDER_CREATE_SUCCESS);

                    // reset the new nominator field after create
                    this.$refs.createOrEdit.resetNewNominatorFields();
                })
                    .catch(e => {
                        this.isSideBarActionLoading = false;
                        errorToast(SOMETHING_WENT_WRONG);
                        console.error(
                            "error while trying to create nominee shareholder",
                            e
                        );
                    });
            }
        };

        function update(data) {
            if (this.isUserAdmin)
            {
                this.isSideBarActionLoading = true;

                if (! data.id)
                {
                    errorToast(REFRESH_AND_TRY_AGAIN_ERROR);
                    return;
                }

                CompanyNomineeShareholder.update(data.id, data).then(() => {
                    this.isNomineeShareholderSidebarActive = false;
                    this.isSideBarActionLoading = false;

                    refNomineeShareholderListTable.value.localItems.forEach(item => {
                        if(item.id === data.id) {
                            Object.keys(data).forEach(key => {
                            // eslint-disable-next-line no-param-reassign
                                item[key] = data[key];
                            });
                        }
                    });
                    this.nomineeShareholder = {};
                
                    successToast(NOMINEE_SHAREHOLDER_UPDATE_SUCCESS);
                })
                    .catch(e => {
                        this.isSideBarActionLoading = false;
                        errorToast(SOMETHING_WENT_WRONG);
                        console.error(
                            "error while trying to update nominee shareholder",
                            e
                        );
                    });
            }
        };

        function remove(data) {
            this.$refs.modal.$refs.confirmationModal.show();
            dataToDelete.value= data;
        };

        function cancelDelete() {
            dataToDelete.value = null;
            this.$refs.modal.$refs.confirmationModal.hide();
        }

        function confirmDelete() {
            if (this.isUserAdmin) {
                this.isSideBarActionLoading = true;

                if (! dataToDelete.value.item.id)
                {
                    errorToast(REFRESH_AND_TRY_AGAIN_ERROR);
                    return;
                }

                CompanyNomineeShareholder.delete(dataToDelete.value.item.id).then(() => {
                    this.isSideBarActionLoading = false;

                    refNomineeShareholderListTable.value.localItems = refNomineeShareholderListTable.value.localItems.filter(item => dataToDelete.value.item.id !== item.id);
                    this.$refs.modal.$refs.confirmationModal.hide();

                    dataToDelete.value = null;
                    successToast(NOMINEE_SHAREHOLDER_DELETE_SUCCESS);
                })
                    .catch(e => {
                        this.isSideBarActionLoading = false;
                        errorToast(SOMETHING_WENT_WRONG);
                        console.error(
                            "error while trying to delete nominee shareholder",
                            e
                        );
                    });
            }
        };

        function approvePendingChangeNominator(changeNominator) {
            if(this.isUserAdmin) {
                GenerateDocument.approveChangeShareholderNominator(changeNominator.id).then(() => {
                    fetchNomineeShareholders();
                    successToast(NOMINATOR_CHANGE_APPROVE_SUCCESS);
                })
                    .catch(e => {
                        if(e.response?.data?.message) {
                            errorToast(e.response?.data?.message);
                                
                        } else {
                            errorToast(SOMETHING_WENT_WRONG);
                            // eslint-disable-next-line no-console
                            console.error("error while trying to approve pending shareholder nominator changes", e);
                        }
                    });
            }
        }

        return {
            refNomineeShareholderListTable,
            refetchData,
            fetchNomineeShareholders,

            // UI
            filterMode,

            // methods
            create,
            update,
            remove,
            approvePendingChangeNominator,
            localizeData,
            confirmDelete, 
            cancelDelete,
            payloadForNomineeShareholderTable,
            payloadForPendingChangeNominatorTable,
        };
    }
};
</script>